/**
 * Guided Builder (M27K)
 * =======================
 * Replaces the Scenarios tab's raw assumptions list with a 5-section
 * completeness checklist + slide-in section drawer. Albert's "I have
 * built budgets for 20 years and have no idea what to do with this"
 * complaint -- the answer is a per-section progress view with explicit
 * UNLOCKS chips + a Next-best-step CTA that fills the highest-leverage
 * gap with one click.
 *
 * Architecture:
 *   - One file by convention (PlanningStudio.tsx pattern: cohesive
 *     subtree, inline styles, named const stylesheets)
 *   - Pulls from GET /api/v1/planning/scenarios/{id}/sections
 *   - Drawer state lives at GuidedBuilder level; rows pass open(key)
 *   - Pathway actions (seed/select-formula/ask/recompute) all bounce
 *     the parent reload via onChanged so the table updates after a
 *     successful write.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_V1, authFetch } from '../../constants';

const API = `${API_V1}/planning`;

// ── Types ───────────────────────────────────────────────────────────

export type SectionStatus = 'LIVE' | 'PART' | 'EMPTY' | 'BLOCKED';

interface FyMetric {
  value: number;
  currency: string;
  formatted: string;
}

interface FormulaInput {
  key: string;
  value: any;
  filled: boolean;
}

interface FormulaAvailable {
  output: string;
  formula_id: string;
  label: string;
  inputs: FormulaInput[];
  expression_preview: string;
  description: string;
  is_active: boolean;
}

interface InputRegistryRow {
  key: string;
  value: any;
  filled: boolean;
  suggested_value: any;
  suggested_source: string | null;
}

interface SectionRow {
  key: string;
  label: string;
  status: SectionStatus;
  fy_metric: FyMetric;
  primary_output: string;
  trajectory_12: (number | null)[];
  sparkline: string;
  active_formula_key: string | null;
  source_tag: string | null;
  source_who: string | null;
  source_when: string | null;
  inputs_filled: number;
  inputs_total: number;
  unlocks: string[];
  blocked_by: string[];
  downstream_cell_count: number;
  formulas_available: FormulaAvailable[];
  input_registry: InputRegistryRow[];
  pathway_copy: { seed: string; ask: string };
  ask_example: string;
}

interface GlobalProgress {
  sections_live: number;
  sections_total: number;
  inputs_filled: number;
  inputs_total: number;
  pct: number;
}

interface SectionsPayload {
  scenario_id: number;
  scenario_name: string;
  fiscal_year: number | null;
  status: string;
  has_computed_outputs: boolean;
  global_progress: GlobalProgress;
  sections: SectionRow[];
}

interface NextBestStep {
  ok: boolean;
  reason?: string;
  section_key?: string;
  section_label?: string;
  driver_key?: string;
  suggested_value?: any;
  suggested_source?: string | null;
  downstream_cell_count?: number;
  copy?: string;
}

// ── Constants ───────────────────────────────────────────────────────

const STATUS_STYLE: Record<SectionStatus, { bg: string; fg: string; border: string; label: string }> = {
  LIVE:    { bg: '#d1fae5', fg: '#047857', border: '#34d399', label: 'LIVE' },
  PART:    { bg: '#fef3c7', fg: '#a16207', border: '#fbbf24', label: 'PART' },
  EMPTY:   { bg: '#f1f5f9', fg: '#475569', border: '#cbd5e1', label: 'EMPTY' },
  BLOCKED: { bg: '#fee2e2', fg: '#991b1b', border: '#fca5a5', label: 'BLOCK' },
};

const SOURCE_BAND_COLORS: Record<string, string> = {
  HUMAN:   '#1e3a5f',
  AI_SEED: '#0d9488',
  IMPORT:  '#7c3aed',
  CLAUDE:  '#8b5cf6',
  DRIVER:  '#f59e0b',
  AUTO:    '#6b7280',
};

const PANEL: React.CSSProperties = {
  background: '#fff',
  border: '1px solid var(--border-subtle, #e2e8f0)',
  borderRadius: 6,
  padding: 16,
  marginBottom: 16,
};

// ── Component ───────────────────────────────────────────────────────

interface Props {
  scenarioId: number;
  scenarioName?: string;
  toast: (msg: string, ok?: boolean) => void;
  onChanged?: () => void;
}

export default function GuidedBuilder({ scenarioId, toast, onChanged }: Props) {
  const [payload, setPayload] = useState<SectionsPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openSectionKey, setOpenSectionKey] = useState<string | null>(null);
  const [nextStep, setNextStep] = useState<NextBestStep | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [secRes, nbsRes] = await Promise.all([
        authFetch(`${API}/scenarios/${scenarioId}/sections`),
        authFetch(`${API}/scenarios/${scenarioId}/next-best-step`),
      ]);
      if (!secRes.ok) {
        const d = await secRes.json().catch(() => ({}));
        throw new Error(d.detail || `failed to load sections (${secRes.status})`);
      }
      const data: SectionsPayload = await secRes.json();
      setPayload(data);
      if (nbsRes.ok) {
        const n = await nbsRes.json();
        setNextStep(n);
      } else {
        setNextStep(null);
      }
    } catch (e: any) {
      console.error('GuidedBuilder load failed', e);
      setError(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  }, [scenarioId]);

  useEffect(() => { load(); }, [load]);

  const refresh = useCallback(() => {
    load();
    onChanged?.();
  }, [load, onChanged]);

  const callPathway = useCallback(async (
    sectionKey: string,
    action: string,
    body: any,
    successMsg: string,
  ) => {
    setBusy(`${sectionKey}:${action}`);
    try {
      const res = await authFetch(
        `${API}/scenarios/${scenarioId}/sections/${sectionKey}/${action}`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body || {}) },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast(data.detail || `${action} failed (${res.status})`, false);
        return null;
      }
      toast(successMsg, true);
      refresh();
      return data;
    } catch (e: any) {
      toast(`${action} failed: ${e?.message || e}`, false);
      return null;
    } finally {
      setBusy(null);
    }
  }, [scenarioId, toast, refresh]);

  const openSection = useMemo(
    () => payload?.sections.find(s => s.key === openSectionKey) || null,
    [payload, openSectionKey],
  );

  if (loading) {
    return <div style={{ padding: 24, color: 'var(--text-hint)' }}>Loading guided builder…</div>;
  }
  if (error || !payload) {
    return (
      <div style={{ ...PANEL, color: '#991b1b' }}>
        Failed to load guided builder: {error || 'no data'}
      </div>
    );
  }

  return (
    <div>
      <BuilderHeader
        payload={payload}
        nextStep={nextStep}
        onSeedAll={async () => {
          // Sequential per-section seed so we don't race the row lock on
          // scen.assumptions. Stops on first failure to surface the error
          // rather than burying it under the next toast.
          for (const sec of payload.sections) {
            const ok = await callPathway(sec.key, 'seed', {}, `${sec.label} seeded`);
            if (!ok) break;
          }
        }}
        onDoThisForMe={async () => {
          if (!nextStep || !nextStep.ok || !nextStep.section_key) return;
          const sec = nextStep.section_key;
          if (nextStep.suggested_value !== null && nextStep.suggested_value !== undefined) {
            await callPathway(sec, 'seed', {}, `${nextStep.section_label} seeded from actuals`);
          } else {
            setOpenSectionKey(sec);
          }
        }}
        busy={busy != null}
      />

      <div style={{ display: 'grid', gridTemplateColumns: openSection ? '1fr 520px' : '1fr', gap: 16 }}>
        <div>
          <SectionsTable
            sections={payload.sections}
            openKey={openSectionKey}
            onOpen={setOpenSectionKey}
            onPathway={callPathway}
            busy={busy}
          />
        </div>

        {openSection && (
          <SectionDrawer
            section={openSection}
            sections={payload.sections}
            onClose={() => setOpenSectionKey(null)}
            onPathway={callPathway}
            onPrev={() => navigatePrevNext(payload.sections, openSectionKey!, -1, setOpenSectionKey)}
            onNext={() => navigatePrevNext(payload.sections, openSectionKey!, +1, setOpenSectionKey)}
            busy={busy}
          />
        )}
      </div>
    </div>
  );
}

// ── Header ─────────────────────────────────────────────────────────

function BuilderHeader({
  payload, nextStep, onSeedAll, onDoThisForMe, busy,
}: {
  payload: SectionsPayload;
  nextStep: NextBestStep | null;
  onSeedAll: () => void;
  onDoThisForMe: () => void;
  busy: boolean;
}) {
  const p = payload.global_progress;
  const pct = Math.round(p.pct * 100);

  return (
    <div style={{ ...PANEL, padding: 0, overflow: 'hidden' }}>
      <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border-subtle, #e2e8f0)' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 16 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy-900)' }}>
              {payload.scenario_name} · Guided Builder
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-hint)', marginTop: 2 }}>
              {p.sections_live} / {p.sections_total} sections live · {p.inputs_filled} / {p.inputs_total} inputs · {pct}%
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <Btn onClick={onSeedAll} disabled={busy}>Seed from actuals</Btn>
          </div>
        </div>
        <div style={{ marginTop: 10, height: 6, borderRadius: 3, background: '#f1f5f9', overflow: 'hidden' }}>
          <div style={{
            width: `${pct}%`, height: '100%',
            background: pct >= 80 ? '#10b981' : pct >= 40 ? '#f59e0b' : '#94a3b8',
            transition: 'width 250ms ease',
          }} />
        </div>
      </div>

      {nextStep && nextStep.ok && nextStep.copy && (
        <div style={{
          padding: '10px 16px',
          background: '#fef9c3',
          borderTop: '1px solid #fde68a',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}>
          <div style={{ fontSize: 12, color: '#713f12' }}>
            <strong>Next best step:</strong> {nextStep.copy}
          </div>
          <Btn primary onClick={onDoThisForMe} disabled={busy}>Do this for me</Btn>
        </div>
      )}
    </div>
  );
}

// ── Sections table ─────────────────────────────────────────────────

function SectionsTable({
  sections, openKey, onOpen, onPathway, busy,
}: {
  sections: SectionRow[];
  openKey: string | null;
  onOpen: (key: string) => void;
  onPathway: (sectionKey: string, action: string, body: any, successMsg: string) => Promise<any>;
  busy: string | null;
}) {
  return (
    <div style={PANEL}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f8fafc' }}>
              <th style={headStyle}>Status</th>
              <th style={headStyle}>Category</th>
              <th style={{ ...headStyle, textAlign: 'right' }}>FY Metric</th>
              <th style={headStyle}>Trajectory</th>
              <th style={headStyle}>Unlocks</th>
              <th style={{ ...headStyle, textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sections.map(s => {
              const isOpen = s.key === openKey;
              const sourceColor = s.source_tag ? SOURCE_BAND_COLORS[s.source_tag] || '#475569' : '#cbd5e1';
              return (
                <React.Fragment key={s.key}>
                  <tr
                    onClick={() => onOpen(s.key)}
                    style={{
                      cursor: 'pointer',
                      background: isOpen ? '#f1f5f9' : 'transparent',
                      borderTop: '1px solid var(--border-subtle, #e2e8f0)',
                      transition: 'background 120ms ease',
                    }}
                  >
                    <td style={cellStyle}>
                      <StatusPill status={s.status} />
                    </td>
                    <td style={cellStyle}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--navy-900)' }}>
                        {s.label}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-hint)', marginTop: 2 }}>
                        {s.inputs_filled} / {s.inputs_total} inputs
                        {s.source_tag && (
                          <>
                            {' · '}
                            <span style={{ color: sourceColor, fontWeight: 600 }}>{s.source_tag}</span>
                            {s.source_who && ` ${s.source_who}`}
                          </>
                        )}
                      </div>
                    </td>
                    <td style={{ ...cellStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      <div style={{ fontWeight: 700, color: 'var(--navy-900)' }}>{s.fy_metric.formatted}</div>
                      {s.active_formula_key && (
                        <div style={{ fontSize: 10, color: 'var(--text-hint)', marginTop: 2 }}>
                          via {prettyFormula(s.active_formula_key)}
                        </div>
                      )}
                    </td>
                    <td style={cellStyle}>
                      <span style={{
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                        fontSize: 14, color: s.status === 'LIVE' ? sourceColor : '#cbd5e1',
                        letterSpacing: 1,
                      }}>
                        {s.sparkline}
                      </span>
                    </td>
                    <td style={cellStyle}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {s.unlocks.length === 0 && (
                          <span style={{ fontSize: 10, color: 'var(--text-hint)' }}>(terminal)</span>
                        )}
                        {s.unlocks.map(u => (
                          <span key={u} style={chipStyle}>{u}</span>
                        ))}
                        {s.blocked_by.length > 0 && (
                          <span style={{ ...chipStyle, background: '#fee2e2', color: '#991b1b', borderColor: '#fca5a5' }}>
                            waiting: {s.blocked_by.join(', ')}
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={{ ...cellStyle, textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                      <div style={{ display: 'inline-flex', gap: 4 }}>
                        <MiniBtn
                          onClick={() => onPathway(s.key, 'seed', {}, `${s.label} seeded`)}
                          disabled={busy === `${s.key}:seed`}
                          title="Seed from actuals"
                        >seed</MiniBtn>
                        <MiniBtn
                          onClick={() => onOpen(s.key)}
                          title="Open section to Ask Claude"
                        >ask</MiniBtn>
                        <MiniBtn
                          onClick={() => onPathway(s.key, 'recompute', {}, `${s.label} recomputed`)}
                          disabled={busy === `${s.key}:recompute`}
                          title="Recompute"
                        >↻</MiniBtn>
                      </div>
                    </td>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-hint)', marginTop: 10 }}>
        Click any row to expand its drawer. Each section's drivers and
        per-section seed / upload / ask shortcuts live in the drawer.
      </div>
    </div>
  );
}

// ── Drawer ──────────────────────────────────────────────────────────

function SectionDrawer({
  section, sections, onClose, onPathway, onPrev, onNext, busy,
}: {
  section: SectionRow;
  sections: SectionRow[];
  onClose: () => void;
  onPathway: (sectionKey: string, action: string, body: any, successMsg: string) => Promise<any>;
  onPrev: () => void;
  onNext: () => void;
  busy: string | null;
}) {
  const idx = sections.findIndex(s => s.key === section.key);
  const prev = idx > 0 ? sections[idx - 1] : null;
  const next = idx < sections.length - 1 ? sections[idx + 1] : null;

  return (
    <div style={{
      ...PANEL,
      position: 'sticky', top: 16, alignSelf: 'flex-start',
      padding: 0, maxHeight: 'calc(100vh - 80px)', overflow: 'auto',
    }}>
      <div style={{
        padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        borderBottom: '1px solid var(--border-subtle, #e2e8f0)', background: '#f8fafc',
      }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy-900)' }}>{section.label}</div>
          <div style={{ fontSize: 11, color: 'var(--text-hint)', marginTop: 2 }}>
            <StatusPill status={section.status} inline />
            {' · '}{section.fy_metric.formatted} FY
            {section.downstream_cell_count > 0 && (
              <> · blocks {section.downstream_cell_count} downstream cells</>
            )}
          </div>
        </div>
        <button
          onClick={onClose}
          style={{
            padding: '4px 8px', fontSize: 12, fontWeight: 600,
            border: '1px solid var(--border-default, #cbd5e1)',
            background: '#fff', borderRadius: 4, cursor: 'pointer',
          }}
        >close ✕</button>
      </div>

      <div style={{ padding: 16 }}>
        {section.formulas_available.length > 0 && (
          <FormulaPathCards
            section={section}
            onSelect={(formulaId) => onPathway(
              section.key, 'select-formula', { formula_id: formulaId },
              `Switched to formula ${prettyFormula(formulaId)}`,
            )}
            busy={busy === `${section.key}:select-formula`}
          />
        )}

        <InputRegistryView section={section} />

        <PathwayCluster
          section={section}
          onPathway={onPathway}
          busy={busy}
        />

        {section.blocked_by.length > 0 && (
          <div style={{
            marginTop: 12, padding: 10, background: '#fef3c7',
            border: '1px solid #fde68a', borderRadius: 4,
            fontSize: 12, color: '#713f12',
          }}>
            <strong>Blocked by:</strong> {section.blocked_by.join(', ')}.
            Fill those upstream sections first so this section's compute lands non-zero.
          </div>
        )}

        <div style={{
          marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--border-subtle, #e2e8f0)',
          display: 'flex', justifyContent: 'space-between', gap: 8,
        }}>
          <Btn onClick={onPrev} disabled={!prev}>← {prev ? prev.label : ''}</Btn>
          <Btn onClick={onNext} disabled={!next}>{next ? next.label : ''} →</Btn>
        </div>
      </div>
    </div>
  );
}

// ── A/B/C formula picker ───────────────────────────────────────────

function FormulaPathCards({
  section, onSelect, busy,
}: {
  section: SectionRow;
  onSelect: (formulaId: string) => Promise<any> | void;
  busy: boolean;
}) {
  // Group by output -- one card row per output that has alternates.
  const byOutput: Record<string, FormulaAvailable[]> = {};
  section.formulas_available.forEach(f => {
    (byOutput[f.output] ||= []).push(f);
  });

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy-900)', marginBottom: 8 }}>
        Pick a formula <span style={{ fontWeight: 400, color: 'var(--text-hint)' }}>
          (one active per output -- others auto-skip)
        </span>
      </div>
      {Object.entries(byOutput).map(([output, formulas]) => (
        <div key={output} style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 11, color: 'var(--text-hint)', marginBottom: 4 }}>
            For <code style={{ fontWeight: 600 }}>{output}</code>:
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 }}>
            {formulas.map(f => (
              <div
                key={f.formula_id}
                onClick={() => !f.is_active && !busy && onSelect(f.formula_id)}
                style={{
                  padding: 10, borderRadius: 4,
                  border: f.is_active ? '2px solid var(--navy-900, #1e3a5f)' : '1px solid var(--border-default, #cbd5e1)',
                  background: f.is_active ? '#f1f5f9' : '#fff',
                  cursor: f.is_active || busy ? 'default' : 'pointer',
                  opacity: busy ? 0.6 : 1,
                }}
                title={f.description}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{
                    width: 12, height: 12, borderRadius: 6,
                    border: '2px solid var(--navy-900, #1e3a5f)',
                    background: f.is_active ? 'var(--navy-900, #1e3a5f)' : 'transparent',
                  }} />
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--navy-900)' }}>
                    {f.label}
                  </span>
                </div>
                <div style={{ fontSize: 10, color: 'var(--text-hint)', marginTop: 4, fontFamily: 'ui-monospace, monospace' }}>
                  {f.expression_preview}
                </div>
                <div style={{ fontSize: 10, color: 'var(--text-hint)', marginTop: 4 }}>
                  inputs: {f.inputs.map(i => i.key).join(', ') || '(none)'}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Input registry ─────────────────────────────────────────────────

function InputRegistryView({ section }: { section: SectionRow }) {
  if (section.input_registry.length === 0) {
    return (
      <div style={{ marginBottom: 16, fontSize: 12, color: 'var(--text-hint)' }}>
        No operator inputs for this section -- all values derive from upstream sections.
      </div>
    );
  }
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy-900)', marginBottom: 8 }}>
        Inputs ({section.inputs_filled} / {section.inputs_total} filled)
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <tbody>
          {section.input_registry.map(inp => (
            <tr key={inp.key} style={{ borderTop: '1px solid var(--border-subtle, #e2e8f0)' }}>
              <td style={{ padding: '8px 4px', fontFamily: 'ui-monospace, monospace', color: 'var(--navy-900)' }}>
                <span style={{ display: 'inline-block', width: 10, color: inp.filled ? '#10b981' : '#cbd5e1' }}>
                  {inp.filled ? '●' : '○'}
                </span>
                {' '}{inp.key}
              </td>
              <td style={{ padding: '8px 4px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                {inp.filled ? <strong>{formatValue(inp.value)}</strong>
                  : <span style={{ color: 'var(--text-hint)' }}>—</span>}
              </td>
              <td style={{ padding: '8px 4px', fontSize: 11, color: 'var(--text-hint)' }}>
                {inp.suggested_value !== null && inp.suggested_value !== undefined && !inp.filled && (
                  <>↗ suggested {formatValue(inp.suggested_value)}
                    {inp.suggested_source && ` (${inp.suggested_source})`}
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ fontSize: 11, color: 'var(--text-hint)', marginTop: 8 }}>
        Use the Section's Seed button to fill suggested values, or open Ask below to type a natural-language ask.
      </div>
    </div>
  );
}

// ── Pathway cluster ────────────────────────────────────────────────

function PathwayCluster({
  section, onPathway, busy,
}: {
  section: SectionRow;
  onPathway: (sectionKey: string, action: string, body: any, successMsg: string) => Promise<any>;
  busy: string | null;
}) {
  const [askDraft, setAskDraft] = useState('');

  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy-900)', marginBottom: 8 }}>
        Fill this section via
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
        <Btn
          primary
          onClick={() => onPathway(section.key, 'seed', {}, `${section.label} seeded from actuals`)}
          disabled={busy === `${section.key}:seed`}
        >📊 Seed {section.label}</Btn>
        <Btn
          onClick={() => onPathway(section.key, 'recompute', {}, `${section.label} recomputed`)}
          disabled={busy === `${section.key}:recompute`}
        >↻ Recompute section</Btn>
      </div>

      <div style={{
        padding: 10, border: '1px solid var(--border-subtle, #e2e8f0)',
        borderRadius: 4, background: '#fafafa',
      }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--navy-900)', marginBottom: 6 }}>
          💬 Ask Claude
        </div>
        <textarea
          value={askDraft}
          onChange={e => setAskDraft(e.target.value)}
          placeholder={section.ask_example}
          style={{
            width: '100%', minHeight: 60, padding: 8, fontSize: 12,
            border: '1px solid var(--border-default, #cbd5e1)', borderRadius: 4,
            fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box',
          }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, alignItems: 'center', gap: 8 }}>
          <div style={{ fontSize: 10, color: 'var(--text-hint)', flex: 1 }}>
            Example: <em>{section.ask_example}</em>
          </div>
          <Btn
            primary
            onClick={async () => {
              if (!askDraft.trim()) return;
              const r = await onPathway(
                section.key, 'ask', { prompt: askDraft },
                `Claude staged proposal for ${section.label}`,
              );
              if (r && r.ok !== false) setAskDraft('');
            }}
            disabled={!askDraft.trim() || busy === `${section.key}:ask`}
          >Stage proposal</Btn>
        </div>
      </div>
    </div>
  );
}

// ── Small components ───────────────────────────────────────────────

function StatusPill({ status, inline }: { status: SectionStatus; inline?: boolean }) {
  const s = STATUS_STYLE[status];
  return (
    <span style={{
      display: inline ? 'inline-block' : 'inline-flex',
      padding: '2px 8px', fontSize: 11, fontWeight: 700, borderRadius: 10,
      background: s.bg, color: s.fg, border: `1px solid ${s.border}`,
      letterSpacing: 0.4,
    }}>{s.label}</span>
  );
}

function Btn({
  children, onClick, primary, disabled,
}: {
  children: React.ReactNode;
  onClick: () => void;
  primary?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: '6px 12px', fontSize: 12, fontWeight: 600,
        borderRadius: 4, cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        background: primary ? 'var(--navy-900, #1e3a5f)' : '#fff',
        color: primary ? '#fff' : 'var(--navy-900, #1e3a5f)',
        border: primary ? 'none' : '1px solid var(--border-default, #cbd5e1)',
      }}
    >{children}</button>
  );
}

function MiniBtn({
  children, onClick, disabled, title,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  title?: string;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        padding: '3px 8px', fontSize: 11, fontWeight: 600,
        border: '1px solid var(--border-default, #cbd5e1)',
        background: '#fff', borderRadius: 3, cursor: disabled ? 'not-allowed' : 'pointer',
        color: 'var(--navy-900, #1e3a5f)', opacity: disabled ? 0.5 : 1,
      }}
    >{children}</button>
  );
}

// ── Styles + helpers ───────────────────────────────────────────────

const headStyle: React.CSSProperties = {
  fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em',
  padding: '8px 10px', color: 'var(--text-hint)', textAlign: 'left',
};
const cellStyle: React.CSSProperties = { fontSize: 13, padding: '10px 10px', verticalAlign: 'top' };
const chipStyle: React.CSSProperties = {
  display: 'inline-block', padding: '2px 6px', fontSize: 10, fontWeight: 600,
  background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1',
  borderRadius: 3,
};

function prettyFormula(id: string): string {
  return id
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())
    .replace(/Pct/g, '%');
}

function formatValue(v: any): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'number') {
    if (Math.abs(v) < 1 && v !== 0) return v.toFixed(4);
    if (Number.isInteger(v)) return v.toLocaleString();
    return v.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
  return String(v);
}

function navigatePrevNext(
  sections: SectionRow[], currentKey: string, dir: -1 | 1,
  setKey: (k: string | null) => void,
) {
  const idx = sections.findIndex(s => s.key === currentKey);
  const ni = idx + dir;
  if (ni < 0 || ni >= sections.length) return;
  setKey(sections[ni].key);
}
